import { Camera, KeyRound, LogOut, Save, UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, authenticatedFileUrl } from "@/lib/api";

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const [name, setName] = useState(user.name);
  const [avatarFileId, setAvatarFileId] = useState(user.avatar_file_id || null);
  const [avatarPreview, setAvatarPreview] = useState(
    authenticatedFileUrl(user.avatar_file_id),
  );
  const [saving, setSaving] = useState(false);
  const [passwords, setPasswords] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const initials = useMemo(
    () =>
      name
        .trim()
        .split(/\s+/)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "ML",
    [name],
  );

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/"))
      return toast.error("Wybierz plik JPG, PNG lub WEBP");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/files", form);
      setAvatarFileId(data.id);
      setAvatarPreview(URL.createObjectURL(file));
      toast.success("Zdjęcie jest gotowe — zapisz profil");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Nie udało się przesłać zdjęcia",
      );
    } finally {
      event.target.value = "";
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", { name, avatar_file_id: avatarFileId });
      await refreshUser();
      toast.success("Profil został zapisany");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Nie udało się zapisać profilu",
      );
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (passwords.new_password !== passwords.confirm_password)
      return toast.error("Nowe hasła nie są takie same");
    if (passwords.new_password.length < 8)
      return toast.error("Nowe hasło musi mieć co najmniej 8 znaków");
    setSaving(true);
    try {
      await api.put("/profile/password", {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPasswords({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      toast.success("Hasło zostało zmienione");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Nie udało się zmienić hasła",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="page profile-settings-page"
      data-testid="profile-settings-page"
    >
      <div className="page-heading">
        <div>
          <span className="eyebrow">TWOJE KONTO</span>
          <h1>Ustawienia profilu</h1>
          <p>Zmień imię, zdjęcie profilowe lub hasło do platformy.</p>
        </div>
      </div>
      <div className="profile-settings-grid">
        <form className="profile-settings-card" onSubmit={saveProfile}>
          <div className="profile-avatar-editor">
            <div className="profile-avatar-preview">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Zdjęcie profilowe" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div>
              <h2>Twój profil</h2>
              <p>JPG, PNG lub WEBP.</p>
              <label className="profile-upload-button">
                <Camera /> ZMIEŃ ZDJĘCIE
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={uploadAvatar}
                  data-testid="profile-avatar-input"
                />
              </label>
            </div>
          </div>
          <label>
            IMIĘ I NAZWISKO
            <div className="profile-input">
              <UserRound />
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                minLength="2"
                required
                data-testid="profile-name-input"
              />
            </div>
          </label>
          <button
            className="gold-button"
            disabled={saving}
            data-testid="save-profile-button"
          >
            <Save /> ZAPISZ PROFIL
          </button>
        </form>
        <form className="profile-settings-card" onSubmit={savePassword}>
          <div className="profile-card-heading">
            <span>
              <KeyRound />
            </span>
            <div>
              <h2>Zmień hasło</h2>
              <p>Użyj co najmniej 8 znaków.</p>
            </div>
          </div>
          <label>
            AKTUALNE HASŁO
            <input
              type="password"
              value={passwords.current_password}
              onChange={(event) =>
                setPasswords((current) => ({
                  ...current,
                  current_password: event.target.value,
                }))
              }
              autoComplete="current-password"
              required
              data-testid="current-password-input"
            />
          </label>
          <label>
            NOWE HASŁO
            <input
              type="password"
              value={passwords.new_password}
              onChange={(event) =>
                setPasswords((current) => ({
                  ...current,
                  new_password: event.target.value,
                }))
              }
              autoComplete="new-password"
              minLength="8"
              required
              data-testid="new-password-input"
            />
          </label>
          <label>
            POWTÓRZ NOWE HASŁO
            <input
              type="password"
              value={passwords.confirm_password}
              onChange={(event) =>
                setPasswords((current) => ({
                  ...current,
                  confirm_password: event.target.value,
                }))
              }
              autoComplete="new-password"
              minLength="8"
              required
              data-testid="confirm-password-input"
            />
          </label>
          <button
            className="gold-button"
            disabled={saving}
            data-testid="save-password-button"
          >
            <KeyRound /> ZMIEŃ HASŁO
          </button>
        </form>
      </div>
      <button
        className="profile-logout"
        onClick={logout}
        data-testid="profile-logout-button"
      >
        <LogOut /> Wyloguj się z platformy
      </button>
    </div>
  );
}
